export const EXTERNAL_AUDIT_TYPES = [
  { value: 'CERTIFICATION', label: '인증심사' },
  { value: 'SURVEILLANCE', label: '사후심사' },
  { value: 'CUSTOMER', label: '고객심사' },
  { value: 'OTHER', label: '기타' },
] as const;

export type ExternalAuditType = (typeof EXTERNAL_AUDIT_TYPES)[number]['value'];

export const EXTERNAL_AUDIT_FINDING_TYPES = [
  { value: 'NC', label: '부적합(NC)' },
  { value: 'OFI', label: '개선기회(OFI)' },
  { value: 'OBS', label: '관찰(OBS)' },
  { value: 'OTHER', label: '기타' },
] as const;

export type ExternalAuditFindingType = (typeof EXTERNAL_AUDIT_FINDING_TYPES)[number]['value'];

export const EXTERNAL_AUDIT_FINDING_STATUSES = [
  { value: 'OPEN', label: '미결' },
  { value: 'CLOSED', label: '종결' },
] as const;

export type ExternalAuditFindingStatus = (typeof EXTERNAL_AUDIT_FINDING_STATUSES)[number]['value'];

export interface ExternalAuditAttachment {
  attachmentId: number;
  fileName: string;
  fileExt?: string | null;
  fileSize?: number | null;
}

export interface ExternalAuditAuditor {
  id: string;
  name: string;
  org: string;
  role: string;
  email: string;
  /** 연락용 휴대폰 (게스트 로그인 ID와는 별개) */
  phone: string;
}

export interface ExternalAuditFinding {
  id: string;
  findingType: ExternalAuditFindingType | string;
  clause: string;
  finding: string;
  correctiveAction: string;
  dueDate: string;
  status: ExternalAuditFindingStatus | string;
  remark: string;
}

export interface ExternalAuditReport {
  reportId: number;
  reportNo?: string | null;
  title: string;
  auditDate?: string | null;
  auditType?: string | null;
  auditOrg?: string | null;
  location?: string | null;
  scopeText?: string | null;
  auditorsJson?: string | null;
  findingsJson?: string | null;
  conclusion?: string | null;
  followUpDue?: string | null;
  remark?: string | null;
  status?: string | null;
  contractId?: number | null;
  authorUserId?: number | null;
  authorName?: string | null;
  reviewerName?: string | null;
  approverName?: string | null;
  approverUserId?: number | null;
  canRevoke?: boolean | null;
  approvalInProgress?: boolean | null;
  pendingApprovalRequestId?: number | null;
  canCancelApproval?: boolean | null;
  attachmentCount?: number | null;
  attachments?: ExternalAuditAttachment[] | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface ExternalAuditReportPayload {
  title: string;
  auditDate?: string | null;
  auditType?: string | null;
  auditOrg?: string | null;
  location?: string | null;
  scopeText?: string | null;
  auditorsJson?: string | null;
  findingsJson?: string | null;
  conclusion?: string | null;
  followUpDue?: string | null;
  remark?: string | null;
  contractId?: number | null;
}

function newId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function createEmptyAuditor(): ExternalAuditAuditor {
  return { id: newId(), name: '', org: '', role: '', email: '', phone: '' };
}

export function createEmptyFinding(): ExternalAuditFinding {
  return {
    id: newId(),
    findingType: 'NC',
    clause: '',
    finding: '',
    correctiveAction: '',
    dueDate: '',
    status: 'OPEN',
    remark: '',
  };
}

export function defaultAuditors(): ExternalAuditAuditor[] {
  return [createEmptyAuditor()];
}

export function defaultFindings(): ExternalAuditFinding[] {
  return [createEmptyFinding()];
}

export function parseAuditors(json?: string | null): ExternalAuditAuditor[] {
  if (!json?.trim()) return defaultAuditors();
  try {
    const parsed = JSON.parse(json) as ExternalAuditAuditor[];
    if (!Array.isArray(parsed) || parsed.length === 0) return defaultAuditors();
    return parsed.map((row) => ({
      id: row.id || newId(),
      name: row.name || '',
      org: row.org || '',
      role: row.role || '',
      email: row.email || '',
      phone: row.phone || '',
    }));
  } catch {
    return defaultAuditors();
  }
}

export function parseFindings(json?: string | null): ExternalAuditFinding[] {
  if (!json?.trim()) return defaultFindings();
  try {
    const parsed = JSON.parse(json) as ExternalAuditFinding[];
    if (!Array.isArray(parsed) || parsed.length === 0) return defaultFindings();
    return parsed.map((row) => ({
      id: row.id || newId(),
      findingType: row.findingType || 'NC',
      clause: row.clause || '',
      finding: row.finding || '',
      correctiveAction: row.correctiveAction || '',
      dueDate: row.dueDate || '',
      status: row.status || 'OPEN',
      remark: row.remark || '',
    }));
  } catch {
    return defaultFindings();
  }
}

export function auditTypeLabel(type?: string | null) {
  const found = EXTERNAL_AUDIT_TYPES.find((t) => t.value === (type || '').toUpperCase());
  return found?.label || type || '-';
}

export function findingTypeLabel(type?: string | null) {
  const found = EXTERNAL_AUDIT_FINDING_TYPES.find((t) => t.value === (type || '').toUpperCase());
  return found?.label || type || '-';
}

export function findingStatusLabel(status?: string | null) {
  const found = EXTERNAL_AUDIT_FINDING_STATUSES.find((t) => t.value === (status || '').toUpperCase());
  return found?.label || status || '-';
}

export function summarizeFindings(findings: ExternalAuditFinding[]) {
  const filled = findings.filter(
    (f) => f.finding.trim() || f.clause.trim() || f.correctiveAction.trim()
  );
  const nc = filled.filter((f) => (f.findingType || '').toUpperCase() === 'NC').length;
  const ofi = filled.filter((f) => (f.findingType || '').toUpperCase() === 'OFI').length;
  const obs = filled.filter((f) => (f.findingType || '').toUpperCase() === 'OBS').length;
  const open = filled.filter((f) => (f.status || 'OPEN').toUpperCase() === 'OPEN').length;
  return { total: filled.length, nc, ofi, obs, open };
}

export function summarizeAuditors(auditors: ExternalAuditAuditor[]) {
  return auditors.filter(
    (a) => a.name.trim() || a.org.trim() || a.email.trim() || a.phone.trim()
  ).length;
}
